import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { allowanceApi, formatCurrency } from '../services/api';

const PEOPLE = ['민규', '하영'];

/* 사람마다 저금통 색을 다르게 준다. 한눈에 누구 것인지 알아보려고. */
const THEME = {
  민규: { body: '#a6d8f5', dark: '#6fb6e0', ear: '#8ecaef', soft: '#eaf6fd',
          blush: '#6fb6e0', nose: '#8ecaef', nostril: '#5a9cc4' },
  하영: { body: '#f9b3cb', dark: '#e995b5', ear: '#f0a0bf', soft: '#fdeaf1',
          blush: '#f57ba6', nose: '#f695b8', nostril: '#c4607f' },
};

/* 동전 한 개가 저금통 입구에서 떨어지는 움직임 */
const drop = keyframes`
  0%   { transform: translate(-50%, -70px) scale(0.6) rotate(0deg); opacity: 0; }
  25%  { opacity: 1; }
  70%  { transform: translate(-50%, 78px) scale(1) rotate(200deg); opacity: 1; }
  85%  { transform: translate(-50%, 66px) scale(1) rotate(240deg); }
  100% { transform: translate(-50%, 74px) scale(1) rotate(260deg); opacity: 0; }
`;

/* 동전이 들어갈 때 저금통이 살짝 눌렸다 펴진다 */
const squish = keyframes`
  0%, 100% { transform: scale(1, 1); }
  30%      { transform: scale(1.06, 0.94); }
  60%      { transform: scale(0.98, 1.02); }
`;

const floatUp = keyframes`
  0%   { transform: translate(-50%, 0); opacity: 0; }
  20%  { opacity: 1; }
  100% { transform: translate(-50%, -46px); opacity: 0; }
`;

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 28px;
`;

const Banks = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const BankCard = styled.div`
  background: ${(p) => p.$soft};
  border-radius: 18px;
  padding: 20px 16px 18px;
  text-align: center;
  position: relative;
  overflow: hidden;
`;

const BankName = styled.div`
  font-weight: bold;
  font-size: 16px;
  color: #444;
  margin-bottom: 4px;
`;

const BankStage = styled.div`
  position: relative;
  width: 190px;
  height: 168px;
  margin: 6px auto 10px;
  ${(p) =>
    p.$bump &&
    css`
      animation: ${squish} 520ms ease;
    `}
`;

const FallingCoin = styled.div`
  position: absolute;
  left: 44.5%;
  top: 30px;
  width: 24px;
  height: 26px;
  border-radius: 50%;
  background: radial-gradient(circle at 34% 30%, #ffe27a, #f5b91e 62%, #d99406);
  border: 2px solid #c88300;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.18);
  animation: ${drop} 900ms cubic-bezier(0.5, 0, 0.7, 1) forwards;
  z-index: 3;
`;

const Pop = styled.div`
  position: absolute;
  left: 50%;
  top: 8px;
  font-weight: bold;
  font-size: 18px;
  color: #e08c00;
  text-shadow: 0 1px 0 #fff;
  white-space: nowrap;
  animation: ${floatUp} 1200ms ease-out forwards;
  z-index: 4;
`;

const Amount = styled.div`
  font-size: 24px;
  font-weight: bold;
  color: #333;
`;

const Unit = styled.div`
  font-size: 11px;
  color: #aaa;
  margin-top: 3px;
`;

const Sub = styled.div`
  font-size: 12.5px;
  color: #777;
  margin-top: 4px;
`;

const SectionTitle = styled.h3`
  font-size: 15px;
  color: #444;
  margin: 0 0 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const LinkBtn = styled.button`
  border: none;
  background: none;
  color: #007bff;
  font-size: 13px;
  cursor: pointer;
  padding: 0;
`;

const HeadActions = styled.span`
  display: flex;
  align-items: center;
  gap: 14px;
`;

const ItemGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 10px;
`;

/* 항목 카드. 누르면 적립. 고치는 일은 '항목 관리' 창에서 한다. */
const ItemCard = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  text-align: left;
  background: #fff;
  border: 1px solid #e6e6e6;
  border-radius: 12px;
  padding: 12px 14px;
  cursor: pointer;
  font-family: inherit;
  transition: border-color 120ms, transform 120ms;

  &:hover {
    border-color: #f5b91e;
    transform: translateY(-1px);
  }

  .emoji { font-size: 22px; }
  .title { font-size: 14px; color: #333; font-weight: bold; }
  .price { font-size: 13px; color: #e08c00; font-weight: bold; margin-top: 2px; }
  .who   { font-size: 11px; color: #999; margin-top: 2px; }
`;

/* 창 머리. 제목은 왼쪽, 닫기는 오른쪽 끝. */
const SheetHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  /* h4 가 아래 여백을 이미 갖고 있으므로 여기서는 붙여 둔다 */
  margin: -4px -6px 0 0;
`;

const CloseBtn = styled.button`
  /* 보이는 X 보다 누르는 자리를 넓게 */
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
  border: none;
  background: none;
  border-radius: 9px;
  color: #bbb;
  cursor: pointer;

  &:hover { background: #f5f5f5; color: #666; }
  svg { display: block; }
`;

/* 항목 관리 창의 한 줄. 누르면 그 항목 고치기로 넘어간다. */
const ManageRow = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  text-align: left;
  background: none;
  border: none;
  border-bottom: 1px solid #f0f0f0;
  padding: 11px 2px;
  cursor: pointer;
  font-family: inherit;

  &:last-child { border-bottom: none; }
  &:hover { background: #fafafa; }

  .emoji { font-size: 19px; flex: none; }
  .name  { flex: 1; min-width: 0; }
  .title { font-size: 14px; color: #333; }
  .who   { font-size: 11.5px; color: #aaa; margin-top: 2px; }
  .price { font-size: 13px; color: #e08c00; font-weight: bold; flex: none; }
  .go    { color: #ccc; flex: none; display: flex; }
`;

/* 목록이 길어져도 창이 화면을 넘지 않게 여기서만 스크롤한다. */
const ManageList = styled.div`
  max-height: 55vh;
  overflow-y: auto;
  margin: 0 -2px;
`;

/* 기록 한 건. 적어 낸 내용은 접어 두고 눌러야 펴진다. */
const Record = styled.div`
  border-bottom: 1px solid #f0f0f0;
  &:last-child { border-bottom: none; }
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 4px;
  font-size: 13.5px;

  .who   { font-weight: bold; color: #555; flex: none; width: 40px; }
  .what  { flex: 1; color: #333; min-width: 0; text-align: left; }
  .amt   { font-weight: bold; color: #e08c00; flex: none; }
  .date  { color: #bbb; font-size: 11.5px; flex: none; }
  .del   { border: none; background: none; color: #ccc; cursor: pointer; flex: none; }
  .del:hover { color: #e05555; }

  /* 적어 낸 내용이 있는 줄만 눌린다. 없으면 그냥 글자다. */
  button.what {
    display: flex;
    align-items: center;
    gap: 6px;
    background: none;
    border: none;
    padding: 0;
    font: inherit;
    color: inherit;
    cursor: pointer;
  }
  button.what .chev { color: #ccc; flex: none; display: flex; }
  button.what:hover .chev { color: #888; }
`;

const RecordDetail = styled.p`
  margin: 0;
  padding: 0 4px 10px 54px;
  font-size: 12.5px;
  line-height: 1.55;
  color: #777;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
`;

const Empty = styled.p`
  color: #999;
  font-size: 13.5px;
  text-align: center;
  padding: 18px 0;
`;

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 16px;
`;

const Sheet = styled.div`
  background: #fff;
  border-radius: 14px;
  padding: 20px;
  width: 100%;
  max-width: 380px;

  h4 { margin: 0 0 14px; font-size: 16px; color: #333; }
  label { display: block; font-size: 12.5px; color: #666; margin: 12px 0 5px; }
  input, select, textarea {
    width: 100%;
    padding: 9px 10px;
    border: 1px solid #ddd;
    border-radius: 8px;
    font-family: inherit;
    font-size: 14px;
    box-sizing: border-box;
  }
  textarea { min-height: 64px; resize: vertical; }
`;

/* 입력칸 밑에 붙는 짧은 설명. label 과 헷갈리지 않게 더 흐리게 둔다. */
const Hint = styled.p`
  margin: 5px 0 0;
  font-size: 11.5px;
  color: #aaa;
  line-height: 1.4;
`;

const Who = styled.div`
  display: flex;
  gap: 8px;
`;

const WhoBtn = styled.button`
  flex: 1;
  padding: 10px;
  border-radius: 8px;
  border: 2px solid ${(p) => (p.$on ? p.$color : '#e0e0e0')};
  background: ${(p) => (p.$on ? p.$soft : '#fff')};
  font-weight: bold;
  font-size: 14px;
  color: #444;
  cursor: pointer;
  font-family: inherit;
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 20px;

  button {
    flex: 1;
    padding: 10px;
    border-radius: 8px;
    border: none;
    font-weight: bold;
    font-size: 14px;
    cursor: pointer;
    font-family: inherit;
  }
  .primary { background: #f5b91e; color: #fff; }
  .ghost   { background: #f0f0f0; color: #555; }
  .danger  { background: #fff; color: #e05555; border: 1px solid #f0c8c8; }
`;

const ErrorText = styled.p`
  color: #d9534f;
  font-size: 12.5px;
  margin: 10px 0 0;
`;

/** 동전 한 개가 얼마짜리인지. 금액이 커지면 단위를 올려 더미가 넘치지 않게 한다. */
const COIN_UNITS = [1000, 5000, 10000, 50000, 100000, 500000];
const MAX_COINS = 42;

export function coinUnitFor(total) {
  return COIN_UNITS.find((u) => total / u <= MAX_COINS) || COIN_UNITS[COIN_UNITS.length - 1];
}

/**
 * 저금통 그림(옆모습). 배 안에 동전이 차곡차곡 쌓인다.
 * 동전 더미 높이가 번 금액에 비례한다 — 얼마나 모았는지 숫자를 읽기 전에 눈으로 보이게.
 */
function PiggyBank({ person, total }) {
  const t = THEME[person];
  const unit = coinUnitFor(total);
  const count = Math.min(Math.floor(total / unit), MAX_COINS);
  const clipId = `belly-${person}`;

  // 아래부터 한 줄에 6개씩, 줄마다 반 칸 어긋나게 놓아 쌓인 티가 나게 한다.
  const coins = [];
  for (let i = 0; i < count; i += 1) {
    const row = Math.floor(i / 6);
    const col = i % 6;
    coins.push({
      key: i,
      cx: 95 - (5 * 14) / 2 + col * 14 + (row % 2 ? 7 : 0),
      cy: 130 - row * 8,
    });
  }

  return (
    <svg viewBox="0 0 200 175" width="190" height="166" role="img"
         aria-label={`${person} 저금통, 동전 ${count}개`}>
      <ellipse cx="100" cy="164" rx="54" ry="7" fill="rgba(0,0,0,0.07)" />
      {/* 발 */}
      <rect x="58" y="130" width="20" height="22" rx="9" fill={t.dark} />
      <rect x="112" y="130" width="20" height="22" rx="9" fill={t.dark} />
      {/* 몸통 */}
      <ellipse cx="98" cy="96" rx="66" ry="50" fill={t.body} />
      <ellipse cx="70" cy="62" rx="20" ry="11" fill="#fff" opacity="0.32" transform="rotate(-20 70 62)" />
      {/* 귀 */}
      <path d="M120 52 q10 -24 26 -12 q10 8 0 20 Z" fill={t.ear} />
      {/* 동전 투입구 */}
      <rect x="74" y="44" width="30" height="7" rx="3.5" fill={t.dark} />
      {/* 속이 비치는 배 — 여기에 동전이 쌓인다 */}
      <ellipse cx="95" cy="108" rx="47" ry="30" fill="#fff" opacity="0.5" />
      <clipPath id={clipId}>
        <ellipse cx="95" cy="108" rx="47" ry="30" />
      </clipPath>
      <g clipPath={`url(#${clipId})`}>
        {coins.map((c) => (
          <g key={c.key}>
            <circle cx={c.cx} cy={c.cy} r="6.2" fill="#f5b91e" stroke="#d99406" strokeWidth="1.4" />
            <circle cx={c.cx - 2} cy={c.cy - 2} r="1.8" fill="#ffe27a" />
          </g>
        ))}
      </g>
      {/* 볼터치 — 배 창을 덮는 흰 겹침 뒤에 두면 흐려지므로 동전 위에 그린다 */}
      <circle cx="141" cy="97" r="7.5" fill={t.blush} opacity="0.5" />
      {/* 코 */}
      <ellipse cx="163" cy="92" rx="15" ry="13" fill={t.nose} />
      <ellipse cx="159" cy="92" rx="2.4" ry="3.6" fill={t.nostril} />
      <ellipse cx="167" cy="92" rx="2.4" ry="3.6" fill={t.nostril} />
      {/* 눈 */}
      <circle cx="136" cy="78" r="6.5" fill="#3d2b22" />
      <circle cx="138.5" cy="75.5" r="2.4" fill="#fff" />
      {/* 꼬리 */}
      <path d="M33 88 q-14 -3 -10 -14 q5 -11 14 -2" stroke={t.ear} strokeWidth="5.5"
            fill="none" strokeLinecap="round" />
    </svg>
  );
}

export default function Allowance() {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // 완료 보고 모달
  const [reporting, setReporting] = useState(null); // 대상 항목
  const [who, setWho] = useState('민규');
  const [proof, setProof] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  // 항목 관리 목록 / 편집 모달
  const [managing, setManaging] = useState(false);
  const [editing, setEditing] = useState(null); // {id?} — id 없으면 새로 만들기
  const [draft, setDraft] = useState({ title: '', amount: '', emoji: '', person: '공용', requirement: '' });

  // 동전 떨어지는 연출
  const [celebrate, setCelebrate] = useState(null); // {person, amount}

  // 최근 기록에서 펼쳐 둔 한 건. 적어 낸 내용이 길어 목록을 밀어내므로 접어 둔다.
  const [openRecord, setOpenRecord] = useState(null);

  const load = useCallback(async () => {
    try {
      const [i, s] = await Promise.all([allowanceApi.getItems(), allowanceApi.getSummary()]);
      setItems(i.data.filter((x) => x.active));
      setSummary(s.data);
      setError(null);
    } catch (e) {
      setError('불러오지 못했습니다. 잠시 후 다시 시도해 주세요.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const monthCounts = useMemo(() => {
    const m = {};
    (summary?.people || []).forEach((p) => { m[p.person] = p; });
    return m;
  }, [summary]);

  async function submitReport() {
    if (!reporting) return;
    // 서버도 같은 것을 막지만, 여기서 먼저 알려주는 편이 빠르다.
    if (reporting.requirement && !proof.trim()) {
      setFormError(`필수 사항을 적어 주세요 — ${reporting.requirement}`);
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      const res = await allowanceApi.createEarning({
        item_id: reporting.id,
        person: who,
        proof: proof.trim(),
        note: note.trim(),
      });
      setReporting(null);
      setProof('');
      setNote('');
      setCelebrate({ person: who, amount: res.data.amount });
      setTimeout(() => setCelebrate(null), 1300);
      await load();
    } catch (e) {
      setFormError(e?.response?.data?.detail || '적립하지 못했습니다.');
    } finally {
      setSaving(false);
    }
  }

  async function saveItem() {
    setSaving(true);
    setFormError(null);
    try {
      const payload = {
        title: draft.title,
        amount: Number(draft.amount) || 0,
        emoji: draft.emoji,
        person: draft.person,
        requirement: draft.requirement,
      };
      if (editing?.id) await allowanceApi.updateItem(editing.id, payload);
      else await allowanceApi.createItem(payload);
      setEditing(null);
      await load();
    } catch (e) {
      setFormError(e?.response?.data?.detail || '저장하지 못했습니다.');
    } finally {
      setSaving(false);
    }
  }

  async function removeItem() {
    if (!editing?.id) return;
    if (!window.confirm(`"${draft.title}" 항목을 없앨까요? 이미 번 기록은 그대로 남습니다.`)) return;
    setSaving(true);
    try {
      await allowanceApi.deleteItem(editing.id);
      setEditing(null);
      await load();
    } finally {
      setSaving(false);
    }
  }

  async function removeEarning(id) {
    if (!window.confirm('이 기록을 지울까요?')) return;
    await allowanceApi.deleteEarning(id);
    await load();
  }

  if (loading) return <Empty>불러오는 중…</Empty>;
  if (error) return <Empty>{error}</Empty>;

  return (
    <Wrap>
      <Banks>
        {PEOPLE.map((person) => {
          const p = monthCounts[person] || {};
          const t = THEME[person];
          const cheer = celebrate?.person === person;
          return (
            <BankCard key={person} $soft={t.soft}>
              <BankName>{person}</BankName>
              <BankStage $bump={cheer}>
                {cheer && <FallingCoin />}
                {cheer && <Pop>+{Number(celebrate.amount).toLocaleString()}원</Pop>}
                <PiggyBank person={person} total={p.month_total || 0} />
              </BankStage>
              <Amount>{formatCurrency(p.month_total || 0)}</Amount>
              <Sub>
                이번 달 {p.month_count || 0}번 · 전체 {formatCurrency(p.total || 0)}
              </Sub>
              <Unit>동전 1개 = {coinUnitFor(p.month_total || 0).toLocaleString()}원</Unit>
            </BankCard>
          );
        })}
      </Banks>

      <div>
        <SectionTitle>
          <span>무엇을 하고 벌까요?</span>
          <HeadActions>
            <LinkBtn onClick={() => setManaging(true)}>항목 관리</LinkBtn>
            <LinkBtn onClick={() => { setDraft({ title: '', amount: '', emoji: '', person: '공용', requirement: '' }); setEditing({}); }}>
              항목 추가
            </LinkBtn>
          </HeadActions>
        </SectionTitle>

        {items.length === 0 ? (
          <Empty>아직 항목이 없어요. “항목 추가”로 첫 항목을 만들어 보세요.</Empty>
        ) : (
          <ItemGrid>
            {items.map((it) => (
              <ItemCard
                key={it.id}
                onClick={() => { setReporting(it); setProof(''); setNote(''); setFormError(null); }}
              >
                <span className="emoji">{it.emoji || '⭐'}</span>
                <span style={{ minWidth: 0 }}>
                  <div className="title">{it.title}</div>
                  <div className="price">{formatCurrency(it.amount)}</div>
                  {it.person !== '공용' && <div className="who">{it.person} 전용</div>}
                </span>
              </ItemCard>
            ))}
          </ItemGrid>
        )}
      </div>

      <div>
        <SectionTitle><span>최근 기록</span></SectionTitle>
        {(summary?.recent || []).length === 0 ? (
          <Empty>아직 번 기록이 없어요.</Empty>
        ) : (
          summary.recent.map((e) => {
            // 이모지를 비워 둔 항목은 카드·목록에서 ⭐ 로 보인다. 여기만 날것으로
            // 두면 같은 항목인데 기록에서만 아이콘이 빠져 보인다.
            const detail = [e.proof, e.note].filter(Boolean).join('\n');
            const open = openRecord === e.id;
            return (
              <Record key={e.id}>
                <Row>
                  <span className="who">{e.person}</span>
                  {detail ? (
                    <button
                      type="button"
                      className="what"
                      aria-expanded={open}
                      title={open ? '접기' : '적어 낸 내용 보기'}
                      onClick={() => setOpenRecord(open ? null : e.id)}
                    >
                      <span>{e.emoji || '⭐'} {e.title}</span>
                      <span className="chev">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true"
                             stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"
                             style={{ transform: open ? 'rotate(180deg)' : 'none' }}>
                          <path d="M5 9l7 7 7-7" />
                        </svg>
                      </span>
                    </button>
                  ) : (
                    <span className="what">{e.emoji || '⭐'} {e.title}</span>
                  )}
                  <span className="amt">+{Number(e.amount).toLocaleString()}</span>
                  <span className="date">{e.earned_on?.slice(5, 10)}</span>
                  <button className="del" title="기록 삭제" onClick={() => removeEarning(e.id)}>×</button>
                </Row>
                {open && <RecordDetail>{detail}</RecordDetail>}
              </Record>
            );
          })
        )}
      </div>

      {/* 완료 보고 */}
      {/* 바깥을 눌러도 닫지 않는다. 적던 내용이 통째로 날아가는 것보다
          닫는 버튼을 한 번 더 누르는 편이 낫다. */}
      {reporting && (
        <Backdrop>
          <Sheet>
            <h4>{reporting.emoji || '⭐'} {reporting.title} · {formatCurrency(reporting.amount)}</h4>

            <label>누가 했나요?</label>
            <Who>
              {PEOPLE.map((n) => (
                <WhoBtn key={n} $on={who === n} $color={THEME[n].dark} $soft={THEME[n].soft}
                        onClick={() => setWho(n)}>
                  {n}
                </WhoBtn>
              ))}
            </Who>

            {reporting.requirement && (
              <>
                <label>필수 사항</label>
                <textarea
                  value={proof}
                  autoFocus
                  placeholder={reporting.requirement}
                  onChange={(ev) => setProof(ev.target.value)}
                />
              </>
            )}

            <label>비고 (선택)</label>
            <textarea
              value={note}
              autoFocus={!reporting.requirement}
              placeholder="예: 3장까지 읽음"
              onChange={(ev) => setNote(ev.target.value)}
            />

            {formError && <ErrorText>{formError}</ErrorText>}

            <Actions>
              <button className="primary" disabled={saving} onClick={submitReport}>
                {saving ? '넣는 중…' : '저금통에 넣기'}
              </button>
              <button className="ghost" disabled={saving} onClick={() => setReporting(null)}>취소</button>
            </Actions>
          </Sheet>
        </Backdrop>
      )}

      {/* 항목 관리 — 목록에서 고칠 것을 고른다 */}
      {managing && (
        <Backdrop>
          <Sheet>
            <SheetHead>
              <h4>항목 관리</h4>
              <CloseBtn title="닫기" aria-label="닫기" onClick={() => setManaging(false)}>
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" aria-hidden="true"
                     stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
                  <path d="M6 6l12 12M18 6L6 18" />
                </svg>
              </CloseBtn>
            </SheetHead>
            {items.length === 0 ? (
              <Empty>아직 항목이 없어요.</Empty>
            ) : (
              <ManageList>
                {items.map((it) => (
                  <ManageRow
                    key={it.id}
                    onClick={() => {
                      setDraft({
                        title: it.title,
                        amount: String(it.amount),
                        emoji: it.emoji,
                        person: it.person,
                        requirement: it.requirement || '',
                      });
                      setFormError(null);
                      setEditing(it);
                    }}
                  >
                    <span className="emoji">{it.emoji || '⭐'}</span>
                    <span className="name">
                      <div className="title">{it.title}</div>
                      {(it.requirement || it.person !== '공용') && (
                        <div className="who">
                          {[it.person !== '공용' ? `${it.person} 전용` : null, it.requirement]
                            .filter(Boolean)
                            .join(' · ')}
                        </div>
                      )}
                    </span>
                    <span className="price">{formatCurrency(it.amount)}</span>
                    <span className="go">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true"
                           stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M9 5l7 7-7 7" />
                      </svg>
                    </span>
                  </ManageRow>
                ))}
              </ManageList>
            )}
          </Sheet>
        </Backdrop>
      )}

      {/* 항목 추가·수정 */}
      {editing && (
        <Backdrop>
          <Sheet>
            <h4>{editing.id ? '항목 고치기' : '새 항목'}</h4>

            <label>이름</label>
            <input value={draft.title} autoFocus placeholder="예: 책 30분 읽기"
                   onChange={(e) => setDraft({ ...draft, title: e.target.value })} />

            <label>단가 (원)</label>
            <input type="number" min="0" value={draft.amount} placeholder="2000"
                   onChange={(e) => setDraft({ ...draft, amount: e.target.value })} />

            <label>이모지 (선택)</label>
            <input value={draft.emoji} placeholder="📚"
                   onChange={(e) => setDraft({ ...draft, emoji: e.target.value })} />

            <label>누가 할 수 있나요?</label>
            <select value={draft.person} onChange={(e) => setDraft({ ...draft, person: e.target.value })}>
              <option value="공용">둘 다</option>
              {PEOPLE.map((n) => <option key={n} value={n}>{n} 전용</option>)}
            </select>

            <label>필수 사항 (선택)</label>
            <input value={draft.requirement} placeholder="예: 유튜브 영상 링크 및 요약"
                   onChange={(e) => setDraft({ ...draft, requirement: e.target.value })} />
            <Hint>적어 두면 이 항목을 완료할 때 그대로 채워야 넘어갑니다.</Hint>

            {formError && <ErrorText>{formError}</ErrorText>}

            <Actions>
              <button className="primary" disabled={saving} onClick={saveItem}>저장</button>
              <button className="ghost" disabled={saving} onClick={() => setEditing(null)}>취소</button>
              {editing.id && <button className="danger" disabled={saving} onClick={removeItem}>삭제</button>}
            </Actions>
          </Sheet>
        </Backdrop>
      )}
    </Wrap>
  );
}
